import { ChangeEvent, useState } from 'react';

import { InputField } from '@raystack/apsara';
import { Modal } from '@studio/components';
import { AxiosError } from 'axios';

import { useCopilotDispatch } from '@src/contexts';
import { editChatName } from '@src/utils/apis/SeekerApis';
import { HISTORY_ATTRS_DEFAULT_VALUE } from '@src/utils/constants/COPILOT_CONSTANTS';
import { RenameChatProps } from '@src/utils/types/CopilotTypes';

export const RenameChatModal = ({
  name,
  chatId,
  open,
  setOpen,
  closePopover
}: RenameChatProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [chatName, setChatName] = useState<string>(name || '');
  const [fieldError, setFieldError] = useState({ status: false, message: '' });
  const [helperText, setHelperText] = useState('');

  const dispatch = useCopilotDispatch();

  const handleChatNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val?.length > 50) {
      setHelperText("Chat name can't have more than 50 characters");
      return;
    }
    setChatName(val);
    setHelperText('');
  };

  const editName = async () => {
    setIsLoading(true);
    try {
      await editChatName(chatId, chatName);
      setOpen(false);
      dispatch({
        historyAttrs: HISTORY_ATTRS_DEFAULT_VALUE
      });
      closePopover();
    } catch (error) {
      setFieldError({
        status: true,
        message: (error as AxiosError)?.response?.data?.error?.message || ''
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      data-test-id='rename-chat-modal'
      show={open}
      title='Rename Chat'
      buttonText='Save'
      loading={isLoading}
      loaderText='Saving...'
      disabled={chatName === '' || isLoading}
      onClick={editName}
      onClose={() => setOpen(false)}
    >
      <InputField
        label='Name'
        value={chatName}
        onChange={handleChatNameChange}
        placeholder='Provide chat name'
        data-test-id='update-chat-name-textfield'
        helperText={helperText}
        error={fieldError.status ? fieldError.message : ''}
        disabled={isLoading}
      />
    </Modal>
  );
};
